import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowLeftRight,
  ChevronDown,
  Building2,
  CreditCard,
  FileText,
  Gauge,
  Hash,
  Mail,
  Map as MapaIcono,
  MapPin,
  Navigation,
  Pencil,
  Percent,
  Phone,
  Plus,
  Power,
  PowerOff,
  User as UsuarioIcono,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { api } from '@/shared/api/cliente';
import { Cargando, CargandoEnLinea } from '@/shared/ui/Cargando';
import { ErrorDeApi } from '@/shared/api/problema';
import type {
  Cliente,
  CuentaDeServicio,
  DeudaParaCobrar,
  FormaDePagoSri,
  ListaDeTarifas,
  PagoDelHistorial,
  Sector,
  Traspaso,
} from '@/shared/api/tipos';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { nombreLegible } from '@/modules/tarifario/nombres';
import { ActaDeTraspaso } from './ActaDeTraspaso';
import { DialogoDeBajaDeCuenta } from './DialogoDeBajaDeCuenta';
import { FacturasDelCliente } from './FacturasDelCliente';
import { DialogoDeCliente } from './DialogoDeCliente';
import { DialogoDeCuenta } from './DialogoDeCuenta';
import { DialogoDeTraspaso } from './DialogoDeTraspaso';

export function PaginaDetalleDeCliente() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();

  const [editandoCliente, setEditandoCliente] = useState(false);
  const [editandoCuenta, setEditandoCuenta] = useState<{ cuenta: CuentaDeServicio | null } | null>(
    null,
  );
  const [dandoDeBaja, setDandoDeBaja] = useState<CuentaDeServicio | null>(null);
  const [traspasando, setTraspasando] = useState<CuentaDeServicio | null>(null);
  const [abiertas, setAbiertas] = useState<ReadonlySet<number>>(new Set());
  const [acta, setActa] = useState<Traspaso | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cliente = useQuery({
    queryKey: ['clientes', id],
    queryFn: () => api.get<Cliente>(`/clientes/${id}`),
    enabled: Boolean(id),
  });

  const cuentas = useQuery({
    queryKey: ['clientes', id, 'cuentas'],
    queryFn: () => api.get<CuentaDeServicio[]>(`/clientes/${id}/cuentas`),
    enabled: Boolean(id),
  });

  const sectores = useQuery({
    queryKey: ['sectores'],
    queryFn: () => api.get<Sector[]>('/sectores'),
  });

  const formasDePago = useQuery({
    queryKey: ['formas-pago-sri'],
    queryFn: () => api.get<FormaDePagoSri[]>('/clientes/formas-pago-sri'),
  });

  const deuda = useQuery({
    queryKey: ['cliente-deuda', id],
    queryFn: () => api.get<DeudaParaCobrar>(`/cobros/clientes/${id}`),
    enabled: Boolean(id) && puede('deudas.consultar'),
  });

  const historial = useQuery({
    queryKey: ['cliente-historial', id],
    queryFn: () => api.get<PagoDelHistorial[]>(`/cobros/clientes/${id}/historial`),
    enabled: Boolean(id) && puede('pagos.ver'),
  });

  const tarifario = useQuery({
    queryKey: ['tarifas'],
    queryFn: () => api.get<ListaDeTarifas>('/tarifas'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['clientes'] });
  }

  async function reactivar(cuenta: CuentaDeServicio) {
    setError(null);
    try {
      await api.post<CuentaDeServicio>(`/cuentas/${cuenta.id}/activar`, {});
      refrescar();
      notificar.exito(`Cuenta ${cuenta.numeroCuenta} reactivada`);
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo reactivar la cuenta';
      setError(mensaje);
      notificar.error(mensaje);
    }
  }

  if (cliente.isPending) {
    return <Cargando titulo="Cargando el cliente" detalle="Trayendo sus predios y exoneraciones" alto="h-64" />;
  }

  if (cliente.isError || !cliente.data) {
    return (
      <div className="rounded-2xl border border-armazon-200 bg-white p-16 text-center">
        <UsuarioIcono className="mx-auto mb-3 size-12 text-armazon-200" aria-hidden />
        <p className="text-sm font-medium uppercase text-armazon-500">Cliente no encontrado</p>
        <Link
          to="/clientes"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-marca-700 hover:underline"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Volver a clientes
        </Link>
      </div>
    );
  }

  const c = cliente.data;
  const lista = cuentas.data ?? [];
  const activas = lista.filter((x) => x.activa);
  const sector = (sectores.data ?? []).find((s) => s.id === c.sectorId);

  const referencia = tarifario.data?.valorDeReferencia ?? 0;
  const cargoMensual = activas.reduce(
    (suma, x) => suma + Math.round((x.tasaPorcentaje ?? 0) * referencia * 100) / 100,
    0,
  );

  const iniciales = c.nombre
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return (
    <>
      <button
        type="button"
        onClick={() => navegar('/clientes')}
        className="mb-5 inline-flex items-center gap-1.5 text-sm uppercase text-armazon-500 transition-colors hover:text-armazon-800"
      >
        <ArrowLeft className="size-4 shrink-0" aria-hidden />
        Volver a clientes
      </button>

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="mb-5 flex flex-col items-start gap-5 rounded-2xl border border-armazon-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center">
        <span className="grid size-16 shrink-0 place-items-center rounded-2xl bg-marca-600 text-xl font-bold text-white">
          {iniciales}
        </span>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold uppercase text-armazon-900">{c.nombre}</h1>

            <button
              type="button"
              onClick={() => setEditandoCliente(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-semibold uppercase text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-800"
            >
              <Pencil className="size-3" aria-hidden />
              Editar
            </button>

            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ring-1 ${
                c.activo
                  ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                  : 'bg-red-50 text-red-600 ring-red-200'
              }`}
            >
              <span
                className={`size-1.5 rounded-full ${c.activo ? 'bg-emerald-500' : 'bg-red-400'}`}
                aria-hidden
              />
              {c.activo ? 'Activo' : 'Inactivo'}
            </span>

            {c.exentoInteresMora && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase text-amber-700 ring-1 ring-amber-200">
                <Percent className="size-3" aria-hidden />
                Exento de mora
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-armazon-500">
            <Renglon icono={Hash} valor={c.identificacion} mono />
            {c.telefono && <Renglon icono={Phone} valor={c.telefono} mono />}
            {c.email && <Renglon icono={Mail} valor={c.email} />}
            {sector && <Renglon icono={MapPin} valor={`${sector.parroquia} · ${sector.canton}`} />}
          </div>
        </div>
      </div>

      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador
          icono={CreditCard}
          tono="azul"
          etiqueta="Cuentas"
          valor={String(lista.length)}
          nota={`${activas.length} ${activas.length === 1 ? 'activa' : 'activas'}`}
        />
        <Indicador
          icono={Gauge}
          tono="ambar"
          etiqueta="Cargo mensual"
          valor={`$${cargoMensual.toFixed(2)}`}
          nota={`Sobre una base de $${referencia.toFixed(2)}`}
          mono
        />

        <Indicador
          icono={FileText}
          tono={deuda.data ? 'indigo' : 'apagado'}
          etiqueta="Facturas sin pagar"
          valor={deuda.data ? String(deuda.data.facturas.length) : '—'}
          nota={
            deuda.data
              ? deuda.data.facturas.length === 0
                ? 'Está al día'
                : `De ${deuda.data.facturas.at(-1)?.periodo} a ${deuda.data.facturas[0]?.periodo}`
              : puede('deudas.consultar')
                ? 'Consultando'
                : 'Sin permiso para consultar deudas'
          }
          mono
        />
        <Indicador
          icono={Wallet}
          tono={deuda.data && deuda.data.total > 0 ? 'ambar' : 'apagado'}
          etiqueta="Deuda pendiente"
          valor={deuda.data ? `$${deuda.data.total.toFixed(2)}` : '—'}
          nota={
            deuda.data
              ? deuda.data.interes > 0
                ? `Incluye $${deuda.data.interes.toFixed(2)} de interés`
                : 'Sin interés por mora'
              : 'Desde recaudación'
          }
          mono
        />
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-3">

        <div className="space-y-5">
          <Tarjeta icono={UsuarioIcono} titulo="Datos personales">
            <div className="grid gap-3">
              <Dato etiqueta="Nombre completo" valor={c.nombre} />
              <Dato etiqueta="Cédula / RUC" valor={c.identificacion} mono />
              <Dato etiqueta="Teléfono" valor={c.telefono} mono />
              <Dato etiqueta="Email" valor={c.email} />
              <Dato etiqueta="Dirección" valor={c.direccion} />
              <Dato
                etiqueta="Forma de pago SRI"
                valor={
                  (formasDePago.data ?? []).find((f) => f.codigo === c.formaPagoEfectiva)?.nombre ??
                  c.formaPagoEfectiva
                }
                nota={
                  c.formaPagoSri == null ? 'Automática por su tipo de identificación' : undefined
                }
              />
            </div>
          </Tarjeta>

          <Tarjeta icono={MapaIcono} titulo="Ubicación">
            <div className="grid grid-cols-2 gap-3">
              <Dato etiqueta="Parroquia" valor={sector?.parroquia} />
              <Dato etiqueta="Cantón" valor={sector?.canton} />
              <Dato etiqueta="Provincia" valor={sector?.provincia} />
              <Dato etiqueta="Código parroquial" valor={sector?.codparr} mono />
              <div className="col-span-2">
                <Dato
                  etiqueta="Zona"
                  valor={sector == null ? null : sector.ruralidad === 'rural' ? 'Rural' : 'Urbana'}
                />
              </div>
            </div>

            {c.ubicado && (
              <div className="mt-4 grid grid-cols-2 gap-3 border-t border-armazon-100 pt-4">
                <Dato etiqueta="Latitud" valor={c.latitud?.toFixed(6)} mono />
                <Dato etiqueta="Longitud" valor={c.longitud?.toFixed(6)} mono />
                <Dato etiqueta="UTM X" valor={c.zutmX?.toFixed(3)} mono />
                <Dato etiqueta="UTM Y" valor={c.zutmY?.toFixed(3)} mono />
              </div>
            )}
          </Tarjeta>
        </div>

        <div className="space-y-5 lg:col-span-2">
          <Tarjeta
            icono={CreditCard}
            titulo={`Cuentas del servicio (${lista.length})`}
            accion={
              <button
                type="button"
                onClick={() => setEditandoCuenta({ cuenta: null })}
                className="group inline-flex w-48 items-center gap-2 rounded-xl border border-armazon-200 bg-white py-1.5 pl-1.5 pr-3 shadow-sm transition-colors hover:border-marca-300 hover:bg-marca-50/40"
              >
                <span className="grid size-6 shrink-0 place-items-center rounded-lg bg-marca-50 text-marca-600 transition-colors group-hover:bg-marca-100">
                  <Plus className="size-3.5" aria-hidden />
                </span>
                <span className="truncate text-xs font-semibold uppercase text-armazon-700 group-hover:text-marca-700">
                  Agregar cuenta
                </span>
              </button>
            }
          >
            {cuentas.isPending ? (
              <CargandoEnLinea detalle="Trayendo sus predios" />
            ) : lista.length === 0 ? (
              <div className="py-8 text-center">
                <CreditCard className="mx-auto mb-2 size-8 text-armazon-200" aria-hidden />
                <p className="text-sm uppercase text-armazon-400">Sin cuentas registradas</p>
                <p className="mt-1 text-xs uppercase text-amber-600">
                  A este cliente no se le factura nada
                </p>
              </div>
            ) : (
              <div className="space-y-2">

                {lista.length > 1 && (
                  <div className="hidden flex-wrap items-center gap-x-4 px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-armazon-400 sm:flex">
                    <span className="w-4 shrink-0" aria-hidden />
                    <span className="w-8 shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1">Cuenta</span>
                    <span className="hidden w-44 shrink-0 lg:block">Categoría</span>
                    <span className="w-14 shrink-0" aria-hidden />
                    <span className="w-20 shrink-0 text-right">Tasa</span>
                    <span className="w-20 shrink-0 text-right">Al mes</span>
                    <span className="w-14 shrink-0" aria-hidden />
                  </div>
                )}

                {lista.map((cuenta) => (
                  <FichaDeCuenta
                    key={cuenta.id}
                    cuenta={cuenta}
                    referencia={referencia}
                    abierta={lista.length === 1 || abiertas.has(cuenta.id)}
                    alAlternar={() =>
                      setAbiertas((previas) => {
                        const siguientes = new Set(previas);
                        if (siguientes.has(cuenta.id)) {
                          siguientes.delete(cuenta.id);
                        } else {
                          siguientes.add(cuenta.id);
                        }
                        return siguientes;
                      })
                    }
                    alEditar={() => setEditandoCuenta({ cuenta })}
                    alTraspasar={() => setTraspasando(cuenta)}
                    alVerActa={setActa}
                    alDarDeBaja={() => setDandoDeBaja(cuenta)}
                    alReactivar={() => void reactivar(cuenta)}
                  />
                ))}
              </div>
            )}
          </Tarjeta>

          <Tarjeta icono={FileText} titulo="Facturas">
            {puede('facturas.ver') ? (
              <FacturasDelCliente identificacion={c.identificacion} />
            ) : (
              <PendienteDeFase
                icono={FileText}
                titulo="Sin permiso para ver las facturas"
                detalle="Pídaselo a quien administre los roles."
              />
            )}
          </Tarjeta>

          <Tarjeta icono={Wallet} titulo="Historial de pagos">
            <HistorialDePagos
              pagos={historial.data ?? []}
              cargando={historial.isLoading}
              permitido={puede('pagos.ver')}
            />
          </Tarjeta>
        </div>
      </div>

      <DialogoDeCliente
        key={`cliente-${c.id}`}
        abierto={editandoCliente}
        cliente={c}
        sectores={sectores.data ?? []}
        formasDePago={formasDePago.data ?? []}
        alCerrar={() => setEditandoCliente(false)}
        alGuardar={refrescar}
      />

      {editandoCuenta !== null && (
        <DialogoDeCuenta
          abierto
          clienteId={c.id}
          cuenta={editandoCuenta.cuenta}
          alCerrar={() => setEditandoCuenta(null)}
          alGuardar={refrescar}
        />
      )}

      {traspasando !== null && (
        <DialogoDeTraspaso
          abierto
          cuenta={traspasando}
          alCerrar={() => setTraspasando(null)}
          alTraspasar={(nueva) => {
            refrescar();
            setActa(nueva);
          }}
        />
      )}

      {acta !== null && <ActaDeTraspaso acta={acta} alCerrar={() => setActa(null)} />}

      <DialogoDeBajaDeCuenta
        key={dandoDeBaja?.id ?? 'ninguna'}
        abierto={dandoDeBaja !== null}
        cuenta={dandoDeBaja}
        alCerrar={() => setDandoDeBaja(null)}
        alGuardar={refrescar}
      />
    </>
  );
}

function Renglon({
  icono: Icono,
  valor,
  mono = false,
}: {
  icono: LucideIcon;
  valor: string;
  mono?: boolean;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Icono className="size-3.5 shrink-0 text-armazon-400" aria-hidden />
      <span className={`truncate ${mono ? 'importe font-semibold text-armazon-700' : ''}`}>
        {valor}
      </span>
    </span>
  );
}

const TONO_INDICADOR: Record<string, { fondo: string; icono: string; valor: string }> = {
  azul: { fondo: 'bg-blue-50', icono: 'text-blue-500', valor: 'text-blue-900' },
  ambar: { fondo: 'bg-amber-50', icono: 'text-amber-500', valor: 'text-amber-800' },
  apagado: { fondo: 'bg-armazon-100', icono: 'text-armazon-300', valor: 'text-armazon-400' },
};

function Indicador({
  icono: Icono,
  tono,
  etiqueta,
  valor,
  nota,
  mono = false,
}: {
  icono: LucideIcon;
  tono: keyof typeof TONO_INDICADOR;
  etiqueta: string;
  valor: string;
  nota: string;
  mono?: boolean;
}) {
  const t = TONO_INDICADOR[tono] ?? TONO_INDICADOR['apagado'];
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-armazon-200 bg-white px-4 py-3 shadow-sm">
      <span className={`grid size-9 shrink-0 place-items-center rounded-xl ${t?.fondo ?? ''}`}>
        <Icono className={`size-[17px] ${t?.icono ?? ''}`} aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-armazon-400">
          {etiqueta}
        </p>
        <p className={`text-lg font-bold leading-tight ${t?.valor ?? ''} ${mono ? 'importe' : ''}`}>
          {valor}
        </p>
        <p className="text-[10px] uppercase text-armazon-400">{nota}</p>
      </div>
    </div>
  );
}

function Tarjeta({
  icono: Icono,
  titulo,
  accion,
  children,
}: {
  icono: LucideIcon;
  titulo: string;
  accion?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-armazon-200 bg-white p-5 shadow-sm">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-armazon-700">
          <Icono className="size-4 shrink-0 text-armazon-400" aria-hidden />
          {titulo}
        </h2>
        {accion}
      </header>
      {children}
    </section>
  );
}

function Dato({
  etiqueta,
  valor,
  nota,
  mono = false,
}: {
  etiqueta: string;
  valor: string | null | undefined;
  nota?: string | undefined;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-armazon-400">
        {etiqueta}
      </p>
      <p className={`break-words text-sm text-armazon-800 ${mono ? 'importe font-medium' : ''}`}>
        {valor ?? <span className="text-armazon-300">—</span>}
      </p>
      {nota && <p className="text-[10px] uppercase text-armazon-400">{nota}</p>}
    </div>
  );
}

function PendienteDeFase({
  icono: Icono,
  titulo,
  detalle,
}: {
  icono: LucideIcon;
  titulo: string;
  detalle: string;
}) {
  return (
    <div className="rounded-xl border border-dashed border-armazon-200 py-8 text-center">
      <Icono className="mx-auto mb-2 size-8 text-armazon-200" aria-hidden />
      <p className="text-sm font-medium uppercase text-armazon-400">{titulo}</p>
      <p className="mx-auto mt-1 max-w-sm text-xs uppercase text-armazon-400">{detalle}</p>
    </div>
  );
}

function FichaDeCuenta({
  cuenta,
  referencia,
  abierta,
  alAlternar,
  alEditar,
  alTraspasar,
  alVerActa,
  alDarDeBaja,
  alReactivar,
}: {
  cuenta: CuentaDeServicio;
  referencia: number;
  abierta: boolean;
  alAlternar: () => void;
  alEditar: () => void;
  alTraspasar: () => void;
  alVerActa: (acta: Traspaso) => void;
  alDarDeBaja: () => void;
  alReactivar: () => void;
}) {
  const alMes =
    cuenta.tasaPorcentaje == null
      ? null
      : Math.round(cuenta.tasaPorcentaje * referencia * 100) / 100;

  return (
    <div
      className={`overflow-hidden rounded-xl border ${
        cuenta.activa
          ? 'border-armazon-100 bg-armazon-50/50'
          : 'border-armazon-200 bg-armazon-50/30'
      }`}
    >
      <button
        type="button"
        onClick={alAlternar}
        aria-expanded={abierta}
        className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left transition-colors hover:bg-white/70"
      >
        <ChevronDown
          className={`size-4 shrink-0 text-armazon-400 transition-transform ${
            abierta ? 'rotate-180' : ''
          }`}
          aria-hidden
        />

        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-blue-100 text-blue-600">
          <Building2 className="size-3.5" aria-hidden />
        </span>

        <span className="importe min-w-0 flex-1 truncate text-sm font-bold text-armazon-900">
          {cuenta.numeroCuenta}
        </span>

        <span className="hidden w-44 shrink-0 truncate text-[11px] uppercase text-armazon-500 lg:block">
          {cuenta.destinoTarifario == null
            ? '—'
            : nombreLegible(cuenta.destinoTarifario).toUpperCase()}
        </span>

        {cuenta.ruralidad && (
          <span
            className={`hidden shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase sm:block ${
              cuenta.ruralidad === 'rural'
                ? 'bg-marca-50 text-marca-700'
                : 'bg-blue-50 text-blue-700'
            }`}
          >
            {cuenta.ruralidad === 'rural' ? 'Rural' : 'Urbana'}
          </span>
        )}

        {cuenta.tasaComoPorcentaje != null && (
          <span className="importe hidden w-20 shrink-0 text-right text-[11px] text-indigo-600 sm:block">
            {cuenta.tasaComoPorcentaje.toFixed(4)} %
          </span>
        )}

        <span className="importe w-20 shrink-0 text-right text-sm font-bold text-armazon-900">
          {alMes == null ? '—' : `$${alMes.toFixed(2)}`}
        </span>

        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${
            cuenta.activa
              ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
              : 'bg-armazon-100 text-armazon-400 ring-armazon-200'
          }`}
        >
          {cuenta.activa ? 'Activa' : 'Inactiva'}
        </span>
      </button>

      {abierta && (
        <div className="border-t border-armazon-100 px-4 pb-4 pt-3">
          {cuenta.direccionServicio && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-armazon-100 bg-white px-3 py-2">
              <Navigation className="mt-0.5 size-3 shrink-0 text-armazon-400" aria-hidden />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-armazon-400">
                  Dirección del predio
                </p>
                <p className="text-xs font-medium text-armazon-700">{cuenta.direccionServicio}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-4">
            <Dato etiqueta="Medidor" valor={cuenta.medidor} mono />
            <Dato etiqueta="Cuenta contrato" valor={cuenta.cuentaContrato} mono />
            <Dato etiqueta="Grupo de consumo" valor={cuenta.grupoConsumo} />
            <Dato
              etiqueta="Categoría tarifaria"
              valor={
                cuenta.destinoTarifario == null
                  ? null
                  : nombreLegible(cuenta.destinoTarifario).toUpperCase()
              }
            />
          </div>

          {!cuenta.activa && cuenta.motivoDesactivacion && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] uppercase text-amber-800">
              Motivo de la baja: {cuenta.motivoDesactivacion}
            </p>
          )}

          <HistorialDeTraspasos cuentaId={cuenta.id} alVerActa={alVerActa} />

          <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-armazon-100 pt-3">
            <BotonDeCuenta icono={Pencil} texto="Editar" alPulsar={alEditar} tono="azul" />
            {cuenta.activa ? (
              <>
                <BotonDeCuenta
                  icono={ArrowLeftRight}
                  texto="Cambiar propietario"
                  alPulsar={alTraspasar}
                  tono="azul"
                />
                <BotonDeCuenta
                  icono={PowerOff}
                  texto="Dar de baja"
                  alPulsar={alDarDeBaja}
                  tono="ambar"
                />
              </>
            ) : (
              <BotonDeCuenta icono={Power} texto="Reactivar" alPulsar={alReactivar} tono="verde" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TONO_BOTON = {
  azul: 'hover:border-blue-300 hover:bg-blue-50/40 [&_span:first-child]:bg-blue-50 [&_span:first-child]:text-blue-600',
  ambar:
    'hover:border-amber-300 hover:bg-amber-50/40 [&_span:first-child]:bg-amber-50 [&_span:first-child]:text-amber-600',
  verde:
    'hover:border-emerald-300 hover:bg-emerald-50/40 [&_span:first-child]:bg-emerald-50 [&_span:first-child]:text-emerald-600',
} as const;

function BotonDeCuenta({
  icono: Icono,
  texto,
  alPulsar,
  tono,
}: {
  icono: LucideIcon;
  texto: string;
  alPulsar: () => void;
  tono: keyof typeof TONO_BOTON;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      className={`inline-flex w-48 items-center gap-2 rounded-xl border border-armazon-200 bg-white py-1.5 pl-1.5 pr-3 shadow-sm transition-colors ${TONO_BOTON[tono]}`}
    >
      <span className="grid size-6 shrink-0 place-items-center rounded-lg transition-colors">
        <Icono className="size-3.5" aria-hidden />
      </span>
      <span className="truncate text-xs font-semibold uppercase text-armazon-700">{texto}</span>
    </button>
  );
}

function HistorialDeTraspasos({
  cuentaId,
  alVerActa,
}: {
  cuentaId: number;
  alVerActa: (acta: Traspaso) => void;
}) {
  const traspasos = useQuery({
    queryKey: ['cuentas', cuentaId, 'traspasos'],
    queryFn: () => api.get<Traspaso[]>(`/cuentas/${cuentaId}/traspasos`),
  });

  const lista = traspasos.data ?? [];
  if (lista.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 border-t border-armazon-100 pt-3">
      <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400">
        <ArrowLeftRight className="size-3 shrink-0" aria-hidden />
        Traspasos ({lista.length})
      </p>

      <ul className="space-y-1.5">
        {lista.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => alVerActa(t)}
              className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white"
            >
              <span className="importe text-[11px] text-armazon-400">{t.fecha}</span>
              <span className="min-w-0 flex-1 truncate text-xs uppercase text-armazon-700">
                {t.nombreAnterior} → {t.nombreNuevo}
              </span>
              {t.conDeuda && (
                <span className="importe shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  ${t.deudaPendiente.toFixed(2)}
                  {t.deudaLiquidada && ' · liquidada'}
                </span>
              )}
              <span className="shrink-0 text-[10px] font-semibold uppercase text-marca-700 underline underline-offset-2">
                Ver acta
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HistorialDePagos({
  pagos,
  cargando,
  permitido,
}: {
  pagos: PagoDelHistorial[];
  cargando: boolean;
  permitido: boolean;
}) {
  if (!permitido) {
    return (
      <p className="py-6 text-center text-xs uppercase text-armazon-400">
        No tiene permiso para ver pagos
      </p>
    );
  }

  if (cargando) {
    return <CargandoEnLinea detalle="Buscando lo que ha pagado" />;
  }

  if (pagos.length === 0) {
    return (
      <PendienteDeFase
        icono={Wallet}
        titulo="No consta ningún pago"
        detalle="Ni en este sistema ni en el anterior."
      />
    );
  }

  return (
    <ul className="divide-y divide-armazon-100">
      {pagos.map((p, i) => (
        <li
          key={`${p.numeroComprobante ?? 'sin'}-${p.fechaPago}-${i}`}
          className="flex items-center justify-between gap-3 py-2.5"
        >
          <span className="min-w-0">
            <span className="importe block truncate text-xs font-semibold text-armazon-800">
              {p.numeroComprobante ?? 'SIN NÚMERO'}
            </span>
            <span className="block text-[10px] uppercase text-armazon-500">
              {enFechaCorta(p.fechaPago)} · {p.cuantasFacturas}{' '}
              {p.cuantasFacturas === 1 ? 'factura' : 'facturas'}
              {p.historico && ' · SISTEMA ANTERIOR'}
            </span>
          </span>
          <span className="shrink-0 text-right">
            <span
              className={`importe block text-xs font-bold ${
                p.estado === 'anulado'
                  ? 'text-armazon-400 line-through'
                  : 'text-armazon-900'
              }`}
            >
              ${p.total.toFixed(2)}
            </span>
            {p.interes > 0 && (
              <span className="importe block text-[10px] text-amber-600">
                incluye ${p.interes.toFixed(2)}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function enFechaCorta(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
